/**
 * «Tutte le celle del run» — every store × article × size one replenishment run recorded, with what
 * became of it, as ONE file for the whole run (client request of Bata Colombia, owner-approved
 * 2026-10-20; platform ADR 6197).
 *
 * **What the file holds.** Every cell of the run's stock ledger: those it wrote a proposal line for,
 * those whose need is under one pair (`need_below_one_unit`: the store already has enough, or its demand is too low, told by its position), those already covered by stock, in transit
 * and on order (`need_zero_in_transit`), those with a need left uncovered and the reason, those whose
 * stock was not reported that day, and those of a run written before the record. And the ranged
 * sizes the run had no forecast for (platform ADR 6182), each in its place among the cells of its
 * store × article, with the outcome «Nessuna previsione» and why: the platform's file carries them
 * since its ADR 6197 amendment (owner ruling 2026-10-23), outcome `size_without_forecast:<reason>`, so
 * this file no longer appends them from a read of its own. `outcome` says which, in words, `explanation` says it in one sentence with the row's
 * own figures, and `outcome_code` keeps the platform's code. **What it does not hold** (owner
 * ruling): the cells the run never reads — an article outside the planning scope (carrier bags), a
 * size not ranged or not recently active, a store not in the store master. The run counts them and
 * stores no cell; the page says so BEFORE the download (`./RunCellsDownload.tsx`).
 *
 * **The columns** are exactly the owner's list of 2026-10-24, in its order, in the CSV, the CSV for «,»
 * decimals and the Excel alike (`RUN_CELLS_FILE_KEYS`): no run id, no absent-reason column and no
 * provenance column in the rows; the workbook's «Tecnico» keeps the file's provenance once.
 *
 * **Derived, never invented** (same request): `in_transit_and_on_order_units` = position − on hand,
 * `position_less_target_units` = position − target, both absent when a term is; the run's
 * parameters (service level, expected-demand quantile, review weeks, default lead time, presentation
 * minimum, on order) read from the run's own provenance, cited in the provenance and carried by the
 * target column's meaning in «Colonne». The workbook opens on «Riepilogo», counted from the rows the
 * file writes, so the two cannot disagree; the CSV is the rows alone.
 *
 * **The rows come from the platform in ONE request** (`GET …/report/replenishment/cells/export`,
 * a streamed RFC 4180 CSV with a BOM, one column per `CellReportRow` field, by name) — the run-wide
 * consolidation lists' pattern (ch05's run-list CSV, ADR 9069): no cursor walk, no ceiling in the
 * browser. The file the planner saves is then built by `src/export` like every other table's: the
 * headers from the column catalogue, the «Columns» sheet, the provenance, CSV and Excel.
 *
 * **Values are the platform's.** An empty cell is `null` — an absence, never a zero; an integer
 * column holds whole numbers or the file is refused (`RunCellsFileError`), never a guess; a decimal
 * stays the platform's text until the file writes it. A required column the file lacks is a
 * refusal; a nullable one it lacks reads `null`; a column this build does not know is ignored.
 *
 * **A file that came up short is never saved** (item E, 2026-10-21). The platform states the rows the
 * file holds in `X-Bata-Row-Count` before the first one (platform ADR 6201); the rows read must equal
 * it, or the download is refused with `ApiIncompleteFileError` and the page says the file arrived
 * incomplete. A connection cut mid-stream is the same refusal (`callOperationCsvStream`). A platform
 * that states no count (before ADR 6201) leaves the transport as the only proof.
 *
 * The CSV reader is the shared `CsvRowReader` of `@/api` (`src/api/csv-rows.ts`), the one reader of the platform's files.
 */

import {
  ApiIncompleteFileError, callOperation, callOperationCsvStream, CsvRowReader, CSV_FORMULA_GUARD, parseCsvText,
  type CsvFile, type CsvProgress, type CsvStream,
} from '@/api';
import {
  cellsSummary, exportCellsReport, runProvenance, type CellReportRow, type CellsSummaryResponse, type RunProvenanceSummary,
} from '@/api/generated';
import {
  buildVersion, createByteSpool, EXCEL_MAX_ROWS, NO_PRINCIPAL, NOT_PUBLISHED, type ByteSpool, type ExportCell, type ExportColumn, type ExportRequest,
  type ProvenanceFilter,
} from '@/export';
import { i18next } from '@/i18n';
import { formatNumber, formatPercent } from '@/lib/format';
import { SIZE_EXPORT_COLUMNS, SIZE_EXPORT_KEYS, sizeExportCells, sizeName } from '@/planning';

import type { ExportScope } from '../manifest';
import {
  citeMeasures, readExportContext, runProvenance as provenanceOfRun, type CitedMeasure, type ExportContext, type Translate,
} from './export-provenance';
import { BOUND_BY_WORDS } from './exports';
import { readScope } from './scope';
import { primePullReasonLabels } from './network-health/pull-reasons';
import { CAUSE_ORDER, NEED_BELOW_ONE_UNIT, causeWords, causeWordsAt, hasPosition } from './network-health/uncovered-cause';
import { nameExportCells, nameExportColumns } from './run-names';

/** The platform's file cannot be read as the run's cells: a required column is missing, or a cell is not its column's type. */
export class RunCellsFileError extends Error {
  constructor(public readonly detail: string) {
    super(`The platform's file of the run's cells cannot be read: ${detail}.`);
    this.name = 'RunCellsFileError';
  }
}

/** What the shared CSV reader raises for a file that ends inside a quoted cell. */
const rowReaderError = (message: string): Error => new RunCellsFileError(message);

/** `text`: required string. `nullable`: string, `null` when empty or when the column is absent. `optionalInteger`: whole number or `null`. */
type FieldKind = 'text' | 'nullable' | 'optionalInteger' | 'optionalBoolean';

/** Every field of a cell, named once: a field the contract gains is a compile error here until it is read. */
const CELL_FIELDS: { readonly [K in keyof CellReportRow]-?: FieldKind } = {
  run_id: 'text', country_key: 'text', as_of: 'nullable', as_of_absent_reason: 'nullable',
  store_key: 'text', store_name: 'nullable', store_name_absent_reason: 'nullable',
  article_key: 'text', article_name: 'nullable', article_name_absent_reason: 'nullable',
  department_family: 'nullable', department_family_absent_reason: 'nullable',
  size_key: 'text', size_code: 'nullable', size_scale: 'nullable', size_run_position: 'optionalInteger', size_absent_reason: 'nullable',
  position_state: 'text', on_hand_units: 'optionalInteger', on_hand_absent_reason: 'nullable', committed_units: 'optionalInteger',
  weekly_demand: 'nullable', weeks_of_cover: 'nullable', target_units: 'nullable', need_units: 'optionalInteger',
  need_absent_reason: 'nullable', outcome: 'text', proposed_units: 'optionalInteger', bound_by: 'nullable',
  // ADR 6200 (platform v0.100.0): the proposal side's facts, the door's horizon, the target's terms, the sweep's flags.
  brand: 'nullable', brand_absent_reason: 'nullable', source_major_category: 'nullable', source_major_category_absent_reason: 'nullable',
  source_category: 'nullable', source_category_absent_reason: 'nullable', source_sub_category: 'nullable', source_sub_category_absent_reason: 'nullable',
  risk: 'nullable', risk_absent_reason: 'nullable', gender: 'nullable', gender_absent_reason: 'nullable',
  sales_l4w_units: 'optionalInteger', sales_l4w_absent_reason: 'nullable', sales_l2w_units: 'optionalInteger', sales_l2w_absent_reason: 'nullable',
  sales_window_start: 'nullable', sales_window_end: 'nullable',
  in_transit_units: 'nullable', in_transit_absent_reason: 'nullable',
  recovered_demand_units: 'nullable', recovered_demand_absent_reason: 'nullable', recovered_fiscal_week: 'nullable', recovery_run_id: 'nullable',
  review_periods: 'optionalInteger', lead_time_periods: 'optionalInteger', lead_time_source: 'nullable', lead_time_days: 'optionalInteger',
  cover_horizon_weeks: 'optionalInteger', cover_horizon_days: 'optionalInteger', cover_horizon_days_absent_reason: 'nullable', door_horizon_absent_reason: 'nullable',
  expected_horizon_demand: 'nullable', service_quantity: 'nullable', presentation_floor_units: 'optionalInteger', target_terms_absent_reason: 'nullable',
  on_order_units: 'optionalInteger', on_order_absent_reason: 'nullable',
  low_cover: 'optionalBoolean', low_cover_absent_reason: 'nullable', cover_observed: 'nullable',
  broken_size_run: 'optionalBoolean', broken_size_run_absent_reason: 'nullable', size_gap_observed: 'nullable',
  // Platform ADR 6197 amendment (2026-10-23): a size with no forecast — what it sold over the window, and the window.
  units_sold_in_window: 'nullable', window_weeks: 'optionalInteger', window_end_week: 'nullable',
  // Platform ADR 6207: what an unmet need lost to. Read, not written to the file's columns (the owner's 46).
  evidence_kind: 'nullable', evidence_currency_code: 'nullable', margin_per_pair: 'nullable', cutoff_margin_per_pair: 'nullable',
  rank_position: 'optionalInteger', candidate_count: 'optionalInteger', selected_count: 'optionalInteger',
  budget_units: 'optionalInteger', spent_units: 'optionalInteger', cost_units: 'optionalInteger',
  pool_units: 'optionalInteger', left_units: 'optionalInteger', ahead_count: 'optionalInteger', missing_inputs: 'nullable', evidence_absent_reason: 'nullable',
};

/**
 * RFC 4180 as the platform writes it (`CsvRowReader`, the one reader of it: `@/api`), a whole text at once. A quoted cell never closed is a
 * `RunCellsFileError`.
 */
export function parseCsv(text: string): string[][] {
  return parseCsvText(text, rowReaderError);
}

function cellOf(kind: FieldKind, name: string, cell: string, line: number): string | number | boolean | null {
  switch (kind) {
    case 'text': return cell.replace(CSV_FORMULA_GUARD, '');
    case 'nullable': return cell === '' ? null : cell.replace(CSV_FORMULA_GUARD, '');
    case 'optionalInteger':
      if (cell === '') return null;
      if (!/^-?\d+$/.test(cell)) throw new RunCellsFileError(`line ${line}: «${name}» is not a whole number`);
      return Number(cell);
    case 'optionalBoolean':
      if (cell === '') return null;
      if (cell !== 'true' && cell !== 'false') throw new RunCellsFileError(`line ${line}: «${name}» is not true or false`);
      return cell === 'true';
    default: { const exhaustive: never = kind; throw new Error(String(exhaustive)); }
  }
}

/** The run's cells, read by field name from the platform's CSV. */
export function cellsFromCsv(text: string): CellReportRow[] {
  const [header, ...body] = parseCsv(text);
  if (header === undefined) throw new RunCellsFileError('the file is empty');
  const cellAt = cellReader(header);
  return body.map((cells, index) => cellAt(cells, index + 2));
}

/** The reader of one line of the platform's file, by field name from its header: (fields, line number) → the cell. */
function cellReader(header: readonly string[]): (cells: readonly string[], line: number) => CellReportRow {
  const at = new Map(header.map((name, index) => [name, index]));
  const present: (readonly [string, FieldKind, number])[] = [];
  const absent: string[] = [];
  for (const [name, kind] of Object.entries(CELL_FIELDS) as [string, FieldKind][]) {
    const index = at.get(name);
    if (index !== undefined) present.push([name, kind, index]);
    else if (kind === 'text') throw new RunCellsFileError(`the column «${name}» is missing`);
    else absent.push(name);
  }
  return (cells, line) => {
    if (cells.length !== header.length) throw new RunCellsFileError(`line ${line} has ${cells.length} cells, the header ${header.length}`);
    const item: Record<string, string | number | boolean | null> = {};
    for (const name of absent) item[name] = null;
    for (const [name, kind, column] of present) item[name] = cellOf(kind, name, cells[column]!, line);
    return item as unknown as CellReportRow;
  };
}

/**
 * The run's cells as the platform's file streams them, one at a time — none is kept. Progress is the
 * cells read against the rows the platform stated; when the file ends, a count other than the stated
 * one is an `ApiIncompleteFileError` (platform ADR 6201), thrown before the caller's file is complete,
 * so a short file is never saved. `limit`: more cells than this is a {@link RunCellsTooManyRowsError}
 * (the stated count is checked before the first row is read), and the connection is closed.
 */
export async function* streamedCells(file: CsvStream, onProgress?: CsvProgress, limit?: number): AsyncGenerator<CellReportRow> {
  if (limit !== undefined && file.statedRows !== null && file.statedRows > limit) {
    await file.cancel();
    throw new RunCellsTooManyRowsError(file.statedRows, limit);
  }
  const reader = new CsvRowReader(rowReaderError);
  let cellAt: ((cells: readonly string[], line: number) => CellReportRow) | null = null;
  let count = 0;
  const report = (): void => onProgress?.(file.statedRows === null ? count : Math.min(count, file.statedRows), file.statedRows);
  onProgress?.(0, file.statedRows);
  let finished = false;
  try {
    const pieces = file.pieces[Symbol.asyncIterator]();
    for (;;) {
      const next = await pieces.next();
      const rows = next.done === true ? reader.end() : reader.push(next.value);
      for (const fields of rows) {
        if (cellAt === null) { cellAt = cellReader(fields); continue; }
        count += 1;
        if (limit !== undefined && count > limit) throw new RunCellsTooManyRowsError(file.statedRows ?? count, limit);
        yield cellAt(fields, count + 1);
      }
      report();
      if (next.done === true) break;
    }
    finished = true;
  } finally {
    // Stopped early — refused, too many rows, or the caller let go: the connection is closed, not left streaming.
    if (!finished) await file.cancel();
  }
  if (cellAt === null) throw new RunCellsFileError('the file is empty');
  if (file.statedRows !== null && count !== file.statedRows) throw new ApiIncompleteFileError(file.status, count, file.statedRows);
}

/**
 * The run holds more cells than an Excel sheet can: Excel opens at most 1,048,576 rows on a sheet (`EXCEL_MAX_ROWS`), and a file
 * it cannot open is worse than none (owner ruling 2026-10-23, `docs/adr/9073`). The one limit the workbook keeps — the CSVs have none.
 * Said before the cells are read, from the count the platform states; `rows` is that count, `limit` the most cells one workbook holds.
 */
export class RunCellsTooManyRowsError extends Error {
  constructor(public readonly rows: number, public readonly limit: number) {
    super(`The run holds ${rows} cells; an Excel sheet holds at most ${limit} of them.`);
    this.name = 'RunCellsTooManyRowsError';
  }
}

// ── the outcome, in words ────────────────────────────────────────────────────

/**
 * The three outcomes that are not an uncovered reason, each with the words the chapter already has
 * for it. Every other code is the platform's `UNCOVERED_REASONS`, worded as the network-health
 * cockpit words it («Perché nessuna riga», `./network-health/uncovered-cause.ts`).
 */
export const OUTCOME_KEYS: Readonly<Record<string, string>> = {
  proposal_line_written: 'inseason.runCells.outcome.proposalLineWritten',
  position_not_reported: 'inseason.why.reason.notReported',
  not_recorded_by_run: 'inseason.why.reason.notRecordedByRun',
  // A size with no forecast, where no row's window is at hand (the summary, the funnel): its reason without the weeks.
  'size_without_forecast:no_sales_in_window': 'inseason.runCells.outcome.noForecastNoSalesRecent',
  'size_without_forecast:forecast_absent': 'inseason.runCells.outcome.noForecastAbsent',
};

/**
 * The platform's outcome for a ranged size with no forecast (its ADR 6197 amendment, 2026-10-23):
 * `size_without_forecast:<reason>`, the reason ADR 6182's (`no_sales_in_window`, `forecast_absent`).
 */
export const SIZE_WITHOUT_FORECAST = 'size_without_forecast';

/** The ADR 6182 reason of a size-with-no-forecast outcome (`''` for the bare record name); `null` for any other outcome. */
export function noForecastReason(outcome: string): string | null {
  if (outcome === SIZE_WITHOUT_FORECAST) return '';
  const prefix = `${SIZE_WITHOUT_FORECAST}:`;
  return outcome.startsWith(prefix) ? outcome.slice(prefix.length) : null;
}

/** The outcome in the reader's words; the code itself where this build has none — never a guess. */
export function outcomeWords(code: string, translate: (key: string) => string): string {
  const key = OUTCOME_KEYS[code] ?? (noForecastReason(code) === null ? undefined : NO_FORECAST_KEY);
  if (key === undefined) return causeWords(code, translate);
  const text = translate(key);
  return text === '' || text === key ? code : text;
}

/**
 * The outcome in words for a cell whose position is known: `need_below_one_unit` reads «Non serve: ha
 * già abbastanza» or «Non serve: domanda troppo bassa» by the position (platform ADR 6204); every other
 * outcome reads as {@link outcomeWords}.
 */
export function outcomeWordsAt(code: string, translate: (key: string) => string, position: number | null): string {
  return code === NEED_BELOW_ONE_UNIT && position !== null ? causeWordsAt(code, translate, position) : outcomeWords(code, translate);
}

/**
 * A ranged size the run had no forecast for (platform ADR 6182) is a row of the file with its own
 * outcome, «Nessuna previsione» and why — the words of the «Taglie senza previsione» rows this file
 * used to append, by the ADR 6182 reason. `outcome_code` keeps the platform's code.
 */
export const NO_FORECAST_KEYS: Readonly<Record<string, string>> = {
  no_sales_in_window: 'inseason.runCells.outcome.noForecastNoSales',
  forecast_absent: 'inseason.runCells.outcome.noForecastAbsent',
};
const NO_FORECAST_KEY = 'inseason.runCells.outcome.noForecast';

/** What a size with no forecast says of itself: its reason, the window's weeks and what it sold over them. */
export interface NoForecastFacts {
  readonly reason: string;
  readonly window_weeks: number | null;
  readonly units_sold_in_window: number | string | null;
}

/** A size-with-no-forecast row's facts, read off the cell; `null` for any other cell. */
export function noForecastFacts(cell: Pick<CellReportRow, 'outcome' | 'window_weeks' | 'units_sold_in_window'>): NoForecastFacts | null {
  const reason = noForecastReason(cell.outcome);
  return reason === null ? null : { reason, window_weeks: cell.window_weeks, units_sold_in_window: cell.units_sold_in_window };
}

/** The no-forecast outcome in words, on the window's weeks; a reason (or window) this build has no words for reads more plainly. */
export function noForecastWords(row: Pick<NoForecastFacts, 'reason' | 'window_weeks'>, translate: Translate): string {
  if (row.window_weeks === null) return translate(OUTCOME_KEYS[`${SIZE_WITHOUT_FORECAST}:${row.reason}`] ?? NO_FORECAST_KEY);
  return translate(NO_FORECAST_KEYS[row.reason] ?? NO_FORECAST_KEY, { weeks: row.window_weeks });
}

// ── the run's parameters (client request of Bata Colombia, 2026-10-21) ────────

/** The registry keys the run's pull resolved, as its provenance records them. */
const PARAMETER_KEYS = {
  service: 'inseason.pull.service_target',
  central: 'inseason.pull.central_level',
  review: 'inseason.pull.review_periods',
  leadTime: 'inseason.pull.lead_time_days',
  minimum: 'inseason.pull.presentation_minimum_units',
  onOrder: 'inseason.pull.accept_unmeasured_on_order',
} as const;

/**
 * The parameters that say how a cell's target was set, as the run resolved them — each `null` when
 * the run's provenance does not record exactly one value for it. Never a default: the file says
 * «non registrato» instead.
 */
export interface RunCellsParameters {
  /** «Livello di servizio»: the probability the target covers the horizon's demand (0.8 = 80%). */
  readonly serviceLevel: number | null;
  /** «Quantile della domanda attesa»: 0.5 is the median. */
  readonly centralLevel: number | null;
  /** «Settimane obiettivo»: the review weeks of the horizon. */
  readonly reviewWeeks: number | null;
  /** «Lead time predefinito (giorni)»: for a store whose master holds no lead time. */
  readonly leadTimeDays: number | null;
  /** «Minimo espositivo», per store × article. */
  readonly presentationMinimum: number | null;
  /** «Accetta l'ordinato non misurato»: `true` — on order is not measured and counted 0. */
  readonly onOrderCountedZero: boolean | null;
}

export const UNREAD_PARAMETERS: RunCellsParameters = {
  serviceLevel: null, centralLevel: null, reviewWeeks: null, leadTimeDays: null, presentationMinimum: null, onOrderCountedZero: null,
};

/** The parameters from the run's provenance read (`null`: the read failed). */
export function runCellsParameters(provenance: RunProvenanceSummary | null): RunCellsParameters {
  if (provenance === null) return UNREAD_PARAMETERS;
  const value = (key: string): number | null => {
    const values = new Set(provenance.run.parameters
      .filter((parameter) => parameter.key === key && parameter.numeric_value !== null && Number.isFinite(parameter.numeric_value))
      .map((parameter) => parameter.numeric_value as number));
    const [only] = [...values];
    return values.size === 1 && only !== undefined ? only : null;
  };
  const onOrder = value(PARAMETER_KEYS.onOrder);
  return {
    serviceLevel: value(PARAMETER_KEYS.service),
    centralLevel: value(PARAMETER_KEYS.central),
    reviewWeeks: value(PARAMETER_KEYS.review),
    leadTimeDays: value(PARAMETER_KEYS.leadTime),
    presentationMinimum: value(PARAMETER_KEYS.minimum),
    onOrderCountedZero: onOrder === null ? null : onOrder >= 1,
  };
}

/**
 * The parameters in one sentence, by their in-app names: the provenance cites it and the target
 * column's meaning carries it, so the «Colonne» sheet says how THIS run set its targets.
 */
export function parametersSentence(parameters: RunCellsParameters | null, translate: Translate, language: string): string {
  if (parameters === null) return translate('inseason.runCells.parametersUnread');
  if (Object.values(parameters).every((value) => value === null)) return translate('inseason.runCells.parametersNotRecorded');
  const absent = translate('inseason.runCells.parameterAbsent');
  const number = (value: number | null): string => (value === null ? absent : formatNumber(value, language));
  return translate('inseason.runCells.parameters', {
    service: parameters.serviceLevel === null ? absent : formatPercent(parameters.serviceLevel, language),
    central: number(parameters.centralLevel),
    median: parameters.centralLevel === 0.5 ? 'yes' : 'no',
    review: number(parameters.reviewWeeks),
    leadTime: number(parameters.leadTimeDays),
    minimum: number(parameters.presentationMinimum),
    onOrder: parameters.onOrderCountedZero === null ? 'unread' : parameters.onOrderCountedZero ? 'zero' : 'notAccepted',
  });
}

// ── the file ─────────────────────────────────────────────────────────────────

export const RUN_CELLS_EXPORT_ID = 'inseason.runCells';
export const RUN_CELLS_TITLE_KEY = 'inseason.runCells.exportTitle';

/**
 * The file's columns, EXACTLY these and in this order — the CSV, the CSV for «,» decimals and the Excel
 * alike, each built from this one list (owner ruling 2026-10-24: the earlier file had too many fields
 * in a muddled order). Who and where, the article, the size, the stock and the need, what became of
 * the cell and why, the sales, the flags, the target's terms, the inbound, the horizon, then the date
 * and the market. Every other field of the platform's file is left out of it: the run's id, the
 * absent-reason columns, the provenance columns, the brief outcome.
 */
export const RUN_CELLS_FILE_KEYS = [
  'store_key', 'store_name', 'article_key', 'article_name', 'department_family',
  'brand', 'source_major_category', 'source_category', 'source_sub_category', 'risk', 'gender',
  'size_code', 'size_scale', 'size_run_position',
  'on_hand_units', 'committed_units', 'weekly_demand', 'weeks_of_cover', 'target_units', 'need_units',
  'outcome_code', 'outcome', 'proposed_units', 'bound_by',
  'in_transit_and_on_order_units', 'position_less_target_units', 'explanation',
  'sales_l4w_units', 'sales_l4w_weekly_avg', 'sales_l2w_units', 'recovered_demand_units',
  'cover_observed', 'low_cover', 'size_gap_observed', 'broken_size_run',
  'expected_horizon_demand', 'service_quantity', 'service_buffer_units', 'presentation_floor_units',
  'on_order_units', 'in_transit_units',
  'cover_horizon_days', 'lead_time_days', 'lead_time_source',
  'as_of', 'country_key',
] as const;

/** A column of the file. */
export type RunCellsFileKey = (typeof RUN_CELLS_FILE_KEYS)[number];

const [STORE_NAME, ARTICLE_NAME] = nameExportColumns(['store', 'article']) as [ExportColumn, ExportColumn];

/** A column of the shared size catalogue, by its key. */
const sizeColumn = (key: string): ExportColumn => SIZE_EXPORT_COLUMNS.find((column) => column.key === key)!;

/**
 * A column where the list puts it: on the data sheet of the workbook too, never moved to «Tecnico» —
 * the owner ruled the same columns in the same order in every format.
 */
function inPlace(column: ExportColumn): ExportColumn {
  const { technical: _technical, provenance: _provenance, ...kept } = column;
  return kept;
}

/**
 * What each column is: its header, unit and meaning, unchanged from the earlier file. `parameters` is
 * the run's parameter sentence, carried by the target's meaning.
 */
function columnCatalogue(parameters: string): Readonly<Record<RunCellsFileKey, ExportColumn>> {
  return {
    ...(Object.fromEntries(FACTS.map((fact) => [fact.key, factColumn(fact)])) as Record<FactKey, ExportColumn>),
    store_key: { key: 'store_key', headerKey: 'inseason.column.store', kind: 'text', freeze: true, descriptionKey: 'inseason.columnGuide.stockPosition.storeKey' },
    store_name: STORE_NAME,
    article_key: { key: 'article_key', headerKey: 'inseason.column.article', kind: 'text', freeze: true, descriptionKey: 'inseason.columnGuide.stockPosition.articleKey' },
    article_name: ARTICLE_NAME,
    department_family: { key: 'department_family', headerKey: 'inseason.reportAttributes.column.departmentFamily', kind: 'text', descriptionKey: 'inseason.columnGuide.reportAttributes.departmentFamily' },
    size_code: sizeColumn('size_code'),
    size_scale: sizeColumn('size_scale'),
    size_run_position: sizeColumn('size_run_position'),
    on_hand_units: { key: 'on_hand_units', headerKey: 'inseason.column.onHand', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.stockPosition.onHand' },
    committed_units: { key: 'committed_units', headerKey: 'inseason.column.committed', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.stockPosition.committed' },
    weekly_demand: { key: 'weekly_demand', headerKey: 'inseason.column.weeklyDemand', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.stockPosition.weeklyDemand' },
    weeks_of_cover: { key: 'weeks_of_cover', headerKey: 'inseason.column.cover', kind: 'number', unit: 'weeks', descriptionKey: 'inseason.columnGuide.stockPosition.cover' },
    target_units: {
      key: 'target_units', headerKey: 'inseason.networkHealth.export.cause.target', kind: 'number', unit: 'pairs',
      descriptionKey: 'inseason.columnGuide.runCells.target', descriptionValues: { parameters },
    },
    need_units: { key: 'need_units', headerKey: 'inseason.column.need', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.networkHealth.columnGuide.cause.needGuide' },
    outcome_code: { key: 'outcome_code', headerKey: 'inseason.column.reasonCode', kind: 'text', descriptionKey: 'inseason.columnGuide.runCells.outcomeCode' },
    outcome: { key: 'outcome', headerKey: 'inseason.runCells.column.outcome', kind: 'label', descriptionKey: 'inseason.columnGuide.runCells.outcome' },
    proposed_units: { key: 'proposed_units', headerKey: 'inseason.runCells.column.proposedUnits', kind: 'integer', unit: 'pairs', total: 'sum', descriptionKey: 'inseason.columnGuide.runCells.proposedUnits' },
    bound_by: { key: 'bound_by', headerKey: 'inseason.column.boundBy', kind: 'label', display: BOUND_BY_WORDS, descriptionKey: 'inseason.columnGuide.runCells.boundBy' },
    in_transit_and_on_order_units: { key: 'in_transit_and_on_order_units', headerKey: 'inseason.runCells.column.inTransitAndOnOrder', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.runCells.inTransitAndOnOrder' },
    position_less_target_units: { key: 'position_less_target_units', headerKey: 'inseason.runCells.column.positionLessTarget', kind: 'number', unit: 'pairs', descriptionKey: 'inseason.columnGuide.runCells.positionLessTarget' },
    explanation: { key: 'explanation', headerKey: 'inseason.runCells.column.explanation', kind: 'text', descriptionKey: 'inseason.columnGuide.runCells.explanation' },
    as_of: { key: 'as_of', headerKey: 'inseason.runCells.column.asOf', kind: 'date', descriptionKey: 'inseason.columnGuide.runCells.asOf' },
    country_key: { key: 'country_key', headerKey: 'inseason.column.country', kind: 'text', descriptionKey: 'inseason.columnGuide.runCells.countryKey' },
  };
}

/** The file's columns, in {@link RUN_CELLS_FILE_KEYS} order. `parameters` is the run's parameter sentence, carried by the target's meaning. */
export function runCellsColumns(parameters: string): readonly ExportColumn[] {
  const catalogue = columnCatalogue(parameters);
  return RUN_CELLS_FILE_KEYS.map((key) => inPlace(catalogue[key]));
}

const NOT_REPORTED: ExportCell = { kind: 'missing', state: 'not-reported' };
const text = (value: string | null): ExportCell => (value === null ? NOT_REPORTED : { kind: 'text', value });
const integer = (value: number | null): ExportCell => (value === null ? NOT_REPORTED : { kind: 'integer', raw: value });
/** A decimal the platform wrote as text: its number, or `null` when it is none — never rounded, never a zero. */
const numberOf = (value: string | null): number | null => {
  if (value === null || value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
/** A decimal the platform wrote as text: its number, or the text itself when it is not one. */
const decimal = (value: string | null): ExportCell => {
  if (value === null) return NOT_REPORTED;
  const number = numberOf(value);
  return number === null ? { kind: 'text', value } : { kind: 'number', raw: number };
};

// ── the platform's facts per cell (ADR 6200, platform v0.100.0) ───────────────

/** The weeks the four-week sales cover — the 28-day window; the weekly average is the sum ÷ 4, as Proposals computes it. */
const WEEKS_IN_L4W = 4;

type FactKind = 'integer' | 'number' | 'text' | 'flag';

/** One fact the cells read publishes, as a column of the file. */
interface Fact {
  readonly key: RunCellsFileKey;
  readonly kind: FactKind;
  readonly headerKey: string;
  readonly descriptionKey: string;
  readonly unit?: string;
  readonly decimals?: number;
  readonly value: (cell: CellReportRow) => string | number | boolean | null;
}

/** The service buffer: the size's service quantity less its expected demand over the horizon. Can be negative. */
export function serviceBuffer(cell: Pick<CellReportRow, 'service_quantity' | 'expected_horizon_demand'>): number | null {
  const service = numberOf(cell.service_quantity);
  const expected = numberOf(cell.expected_horizon_demand);
  return service === null || expected === null ? null : service - expected;
}

/** The facts the file writes; their place is {@link RUN_CELLS_FILE_KEYS}'s. */
const FACTS = [
  { key: 'sales_l4w_units', kind: 'integer', unit: 'pairs', headerKey: 'inseason.reportAttributes.column.salesL4wUnits', descriptionKey: 'inseason.columnGuide.reportAttributes.salesL4wUnits', value: (c) => c.sales_l4w_units },
  { key: 'sales_l4w_weekly_avg', kind: 'number', unit: 'pairs', decimals: 1, headerKey: 'inseason.reportAttributes.column.salesL4wWeeklyAvg', descriptionKey: 'inseason.columnGuide.reportAttributes.salesL4wWeeklyAvg', value: (c) => (c.sales_l4w_units === null ? null : c.sales_l4w_units / WEEKS_IN_L4W) },
  { key: 'sales_l2w_units', kind: 'integer', unit: 'pairs', headerKey: 'inseason.reportAttributes.column.salesL2wUnits', descriptionKey: 'inseason.columnGuide.reportAttributes.salesL2wUnits', value: (c) => c.sales_l2w_units },
  { key: 'in_transit_units', kind: 'number', unit: 'pairs', headerKey: 'inseason.runCells.column.inTransit', descriptionKey: 'inseason.columnGuide.runCells.inTransit', value: (c) => c.in_transit_units },
  { key: 'lead_time_days', kind: 'integer', headerKey: 'inseason.reportAttributes.column.leadTimeDays', descriptionKey: 'inseason.columnGuide.reportAttributes.leadTimeDays', value: (c) => c.lead_time_days },
  { key: 'lead_time_source', kind: 'text', headerKey: 'inseason.reportAttributes.column.leadTimeSource', descriptionKey: 'inseason.columnGuide.reportAttributes.leadTimeSource', value: (c) => c.lead_time_source },
  { key: 'cover_horizon_days', kind: 'integer', headerKey: 'inseason.reportAttributes.column.coverHorizonDays', descriptionKey: 'inseason.columnGuide.runCells.coverHorizonDays', value: (c) => c.cover_horizon_days },
  { key: 'expected_horizon_demand', kind: 'number', unit: 'pairs', headerKey: 'inseason.runCells.column.expectedHorizonDemand', descriptionKey: 'inseason.columnGuide.runCells.expectedHorizonDemand', value: (c) => c.expected_horizon_demand },
  { key: 'service_quantity', kind: 'number', unit: 'pairs', headerKey: 'inseason.runCells.column.serviceQuantity', descriptionKey: 'inseason.columnGuide.runCells.serviceQuantity', value: (c) => c.service_quantity },
  { key: 'service_buffer_units', kind: 'number', unit: 'pairs', headerKey: 'inseason.runCells.column.serviceBuffer', descriptionKey: 'inseason.columnGuide.runCells.serviceBuffer', value: serviceBuffer },
  { key: 'presentation_floor_units', kind: 'integer', unit: 'pairs', headerKey: 'inseason.runCells.column.presentationFloor', descriptionKey: 'inseason.columnGuide.runCells.presentationFloor', value: (c) => c.presentation_floor_units },
  { key: 'on_order_units', kind: 'integer', unit: 'pairs', headerKey: 'inseason.column.onOrder', descriptionKey: 'inseason.columnGuide.runCells.onOrder', value: (c) => c.on_order_units },
  { key: 'low_cover', kind: 'flag', headerKey: 'inseason.runCells.column.lowCover', descriptionKey: 'inseason.columnGuide.runCells.lowCover', value: (c) => c.low_cover },
  { key: 'cover_observed', kind: 'number', unit: 'weeks', headerKey: 'inseason.runCells.column.coverObserved', descriptionKey: 'inseason.columnGuide.runCells.coverObserved', value: (c) => c.cover_observed },
  { key: 'broken_size_run', kind: 'flag', headerKey: 'inseason.runCells.column.brokenSizeRun', descriptionKey: 'inseason.columnGuide.runCells.brokenSizeRun', value: (c) => c.broken_size_run },
  { key: 'size_gap_observed', kind: 'number', unit: 'pairs', headerKey: 'inseason.runCells.column.sizeGapObserved', descriptionKey: 'inseason.columnGuide.runCells.sizeGapObserved', value: (c) => c.size_gap_observed },
  // The article's attributes with the Proposals file's own headers and words, then the recovered demand.
  { key: 'brand', kind: 'text', headerKey: 'inseason.reportAttributes.column.brand', descriptionKey: 'inseason.columnGuide.reportAttributes.brand', value: (c) => c.brand },
  { key: 'source_major_category', kind: 'text', headerKey: 'inseason.reportAttributes.column.sourceMajorCategory', descriptionKey: 'inseason.columnGuide.reportAttributes.sourceMajorCategory', value: (c) => c.source_major_category },
  { key: 'source_category', kind: 'text', headerKey: 'inseason.reportAttributes.column.sourceCategory', descriptionKey: 'inseason.columnGuide.reportAttributes.sourceCategory', value: (c) => c.source_category },
  { key: 'source_sub_category', kind: 'text', headerKey: 'inseason.reportAttributes.column.sourceSubCategory', descriptionKey: 'inseason.columnGuide.reportAttributes.sourceSubCategory', value: (c) => c.source_sub_category },
  { key: 'risk', kind: 'text', headerKey: 'inseason.reportAttributes.column.risk', descriptionKey: 'inseason.columnGuide.reportAttributes.risk', value: (c) => c.risk },
  { key: 'gender', kind: 'text', headerKey: 'inseason.reportAttributes.column.gender', descriptionKey: 'inseason.columnGuide.reportAttributes.gender', value: (c) => c.gender },
  { key: 'recovered_demand_units', kind: 'number', unit: 'pairs', headerKey: 'inseason.runCells.column.recoveredDemand', descriptionKey: 'inseason.columnGuide.runCells.recoveredDemand', value: (c) => c.recovered_demand_units },
] as const satisfies readonly Fact[];

type FactKey = (typeof FACTS)[number]['key'];

function factColumn(fact: Fact): ExportColumn {
  return {
    key: fact.key, headerKey: fact.headerKey, descriptionKey: fact.descriptionKey, kind: fact.kind === 'flag' ? 'label' : fact.kind,
    ...(fact.unit === undefined ? {} : { unit: fact.unit }),
    ...(fact.decimals === undefined ? {} : { decimals: fact.decimals }),
  };
}

function factCell(fact: Fact, value: string | number | boolean | null): ExportCell {
  if (value === null || value === '') return NOT_REPORTED;
  switch (fact.kind) {
    case 'flag': return { kind: 'label', value: String(value), code: String(value) };
    case 'integer': return { kind: 'integer', raw: Number(value) };
    case 'number': {
      const raw = typeof value === 'number' ? value : numberOf(String(value));
      return raw === null ? { kind: 'text', value: String(value) } : { kind: 'number', raw };
    }
    default: return { kind: 'text', value: String(value) };
  }
}

/** The facts of one cell, by key; an absent one is not reported. Pure. */
function factCells(cell: CellReportRow): Record<FactKey, ExportCell> {
  return Object.fromEntries(FACTS.map((fact: Fact) => [fact.key, factCell(fact, fact.value(cell))])) as Record<FactKey, ExportCell>;
}

/** The columns as a pure caller sees them, the run's parameters not read. */
export const RUN_CELLS_COLUMNS: readonly ExportColumn[] = runCellsColumns('');

// ── the derived figures and the sentence ─────────────────────────────────────

/**
 * In transit plus on order: the position less what is on the shelf. `null` when either is absent —
 * a negative count reads no on-hand figure, and its position keeps the sign, so the difference would
 * be no quantity of stock on the way.
 */
export function inTransitAndOnOrder(cell: Pick<CellReportRow, 'committed_units' | 'on_hand_units'>): number | null {
  return cell.committed_units === null || cell.on_hand_units === null ? null : cell.committed_units - cell.on_hand_units;
}

/** The position less the target: nought or more, the target is reached and there is no need. `null` when either is absent. */
export function positionLessTarget(cell: Pick<CellReportRow, 'committed_units' | 'target_units'>): number | null {
  const target = numberOf(cell.target_units);
  return cell.committed_units === null || target === null ? null : cell.committed_units - target;
}

const NEED_ZERO_IN_TRANSIT = 'need_zero_in_transit';
const LINE_WRITTEN = 'proposal_line_written';
const POSITION_NOT_REPORTED = 'position_not_reported';
const NOT_RECORDED_BY_RUN = 'not_recorded_by_run';

/** Every key of a cell's sentence and the placeholders it takes: plain strings, no plural, no select. */
const CELL_SENTENCES: Readonly<Record<string, readonly string[]>> = {
  'inseason.runCells.explain.position': ['position', 'onHand', 'inbound'],
  'inseason.runCells.explain.positionBare': ['position'],
  'inseason.runCells.explain.proposal': ['proposed', 'need', 'target', 'position'],
  'inseason.runCells.explain.proposalBare': ['proposed'],
  'inseason.runCells.explain.enough': ['target', 'position'],
  'inseason.runCells.explain.lowDemand': ['target', 'position'],
  'inseason.runCells.explain.covered': ['target', 'position'],
  'inseason.runCells.explain.notReported': [],
  'inseason.runCells.explain.notRecorded': [],
  'inseason.runCells.explain.stopped': ['need', 'target', 'position', 'reason'],
  'inseason.runCells.explain.stoppedBare': ['reason'],
  'inseason.runCells.explain.target': ['target'],
  'inseason.runCells.explain.targetTerms': ['demand', 'buffer', 'target'],
  'inseason.runCells.explain.targetTermsFloor': ['demand', 'buffer', 'service', 'floor', 'target'],
};

// Two private-use characters: no catalogue text holds them, and neither is a control character.
const MARK_OPEN = '\uE000';
const MARK_CLOSE = '\uE001';
const MARKED = /\uE000(\w+)\uE001/;

/** Fills a cell's sentence: its catalogue words, with each placeholder's string in its place. */
export type Sentences = (key: string, values?: Readonly<Record<string, string>>) => string;

/**
 * The cells' sentences, each translated ONCE with a marker in every placeholder and then filled per
 * row. The words are the catalogue's, exactly as `translate` gives them — an ICU argument holding a
 * string is inserted as it is — at the cost of a join instead of a catalogue lookup: a run is about
 * 450,000 sentences, and translating each one took half a minute in the browser.
 */
export function sentences(translate: Translate): Sentences {
  const made = new Map<string, readonly string[]>();
  return (key, values = {}) => {
    let parts = made.get(key);
    if (parts === undefined) {
      const names = CELL_SENTENCES[key] ?? [];
      parts = translate(key, Object.fromEntries(names.map((name) => [name, [MARK_OPEN, name, MARK_CLOSE].join('')]))).split(MARKED);
      made.set(key, parts);
    }
    return parts.map((part, index) => (index % 2 === 1 ? values[part] ?? '' : part)).join('');
  };
}

/**
 * Why the cell came out as it did, in one sentence with the cell's own figures — the client's
 * «Nessuna proposta: obiettivo 0 paia ≤ posizione 3 paia», «Fabbisogno 2 paia, …». Every figure is
 * one of the row's columns; a sentence that needs a figure the row lacks is the shorter one that
 * does not state it, never a figure made up. Pure.
 */
export function explanation(cell: CellReportRow, translate: Translate, language: string, outcome: string, say: Sentences = sentences(translate)): string {
  const unforecast = noForecastFacts(cell);
  if (unforecast !== null) return noForecastExplanation(unforecast, translate, language);
  const pairs = (value: number): string => formatNumber(value, language);
  const target = numberOf(cell.target_units);
  const inbound = inTransitAndOnOrder(cell);
  const position = cell.committed_units === null
    ? null
    : inbound === null || cell.on_hand_units === null
      ? say('inseason.runCells.explain.positionBare', { position: pairs(cell.committed_units) })
      : say('inseason.runCells.explain.position', {
        position: pairs(cell.committed_units), onHand: pairs(cell.on_hand_units), inbound: pairs(inbound),
      });
  const figures = target !== null && position !== null;
  // The target, with the terms it was set from when the run stored them (ADR 6200): «domanda X + scorta Y → obiettivo Z».
  const demand = numberOf(cell.expected_horizon_demand);
  const service = numberOf(cell.service_quantity);
  const buffer = serviceBuffer(cell);
  const floor = cell.presentation_floor_units;
  const targetWords = target === null
    ? ''
    : demand === null || buffer === null || service === null
      ? say('inseason.runCells.explain.target', { target: pairs(target) })
      : floor !== null && floor > service
        ? say('inseason.runCells.explain.targetTermsFloor', { demand: pairs(demand), buffer: pairs(buffer), service: pairs(service), floor: pairs(floor), target: pairs(target) })
        : say('inseason.runCells.explain.targetTerms', { demand: pairs(demand), buffer: pairs(buffer), target: pairs(target) });
  switch (cell.outcome) {
    case LINE_WRITTEN:
      return cell.proposed_units === null
        ? outcome
        : figures && cell.need_units !== null
          ? say('inseason.runCells.explain.proposal', { proposed: pairs(cell.proposed_units), need: pairs(cell.need_units), target: targetWords, position })
          : say('inseason.runCells.explain.proposalBare', { proposed: pairs(cell.proposed_units) });
    case NEED_BELOW_ONE_UNIT:
      return !figures ? outcome
        : hasPosition(cell.committed_units)
          ? say('inseason.runCells.explain.enough', { target: targetWords, position })
          : say('inseason.runCells.explain.lowDemand', { target: targetWords, position });
    case NEED_ZERO_IN_TRANSIT:
      return figures ? say('inseason.runCells.explain.covered', { target: targetWords, position }) : outcome;
    case POSITION_NOT_REPORTED:
      return say('inseason.runCells.explain.notReported');
    case NOT_RECORDED_BY_RUN:
      return say('inseason.runCells.explain.notRecorded');
    default:
      return figures && cell.need_units !== null && cell.need_units > 0
        ? say('inseason.runCells.explain.stopped', { need: pairs(cell.need_units), target: targetWords, position, reason: outcome })
        : say('inseason.runCells.explain.stoppedBare', { reason: outcome });
  }
}

/** A ranged size with no forecast, in one sentence: what it sold over the window, and so no target. A figure it lacks is not stated. */
export function noForecastExplanation(row: NoForecastFacts, translate: Translate, language: string): string {
  const sold = row.units_sold_in_window === null ? null : Number(row.units_sold_in_window);
  if (row.window_weeks !== null && row.reason === 'no_sales_in_window') return translate('inseason.runCells.explain.noForecastNoSales', { weeks: row.window_weeks });
  if (row.window_weeks !== null && row.reason === 'forecast_absent' && sold !== null && Number.isFinite(sold)) {
    return translate('inseason.runCells.explain.noForecastAbsent', { weeks: row.window_weeks, sold: formatNumber(sold, language) });
  }
  return translate('inseason.runCells.explain.noForecast');
}

/** Where each size field sits among the shared size cells (`sizeExportCells`). */
const SIZE_AT = {
  size_code: SIZE_EXPORT_KEYS.indexOf('size_code'),
  size_scale: SIZE_EXPORT_KEYS.indexOf('size_scale'),
  size_run_position: SIZE_EXPORT_KEYS.indexOf('size_run_position'),
} as const;

/** One cell of the run as a row of {@link RUN_CELLS_COLUMNS}: each column's value by key, written in {@link RUN_CELLS_FILE_KEYS} order. Pure. */
export function runCellRow(
  cell: CellReportRow, translate: Translate, language = 'en-GB',
  words: (code: string, position: number | null, weeks?: number | null) => string = (code, position, weeks) => cellOutcomeWords(code, translate, position, weeks ?? null),
  say: Sentences = sentences(translate),
): readonly ExportCell[] {
  const [storeName, articleName] = nameExportCells([
    { code: cell.store_key, name: cell.store_name, reason: cell.store_name === null ? cell.store_name_absent_reason : null },
    { code: cell.article_key, name: cell.article_name, reason: cell.article_name === null ? cell.article_name_absent_reason : null },
  ]) as [ExportCell, ExportCell];
  const size = sizeExportCells(sizeName(cell));
  const outcome = words(cell.outcome, cell.committed_units, cell.window_weeks);
  const gap = positionLessTarget(cell);
  const cells: Readonly<Record<RunCellsFileKey, ExportCell>> = {
    ...factCells(cell),
    store_key: { kind: 'text', value: cell.store_key },
    store_name: storeName,
    article_key: { kind: 'text', value: cell.article_key },
    article_name: articleName,
    department_family: text(cell.department_family),
    size_code: size[SIZE_AT.size_code]!,
    size_scale: size[SIZE_AT.size_scale]!,
    size_run_position: size[SIZE_AT.size_run_position]!,
    on_hand_units: integer(cell.on_hand_units),
    committed_units: integer(cell.committed_units),
    weekly_demand: decimal(cell.weekly_demand),
    weeks_of_cover: decimal(cell.weeks_of_cover),
    target_units: decimal(cell.target_units),
    need_units: integer(cell.need_units),
    outcome_code: { kind: 'text', value: cell.outcome },
    outcome: { kind: 'label', value: outcome, code: cell.outcome },
    proposed_units: integer(cell.proposed_units),
    bound_by: cell.bound_by === null || cell.bound_by === '' ? NOT_REPORTED : { kind: 'label', value: cell.bound_by, code: cell.bound_by },
    in_transit_and_on_order_units: integer(inTransitAndOnOrder(cell)),
    position_less_target_units: gap === null ? NOT_REPORTED : { kind: 'number', raw: gap },
    explanation: { kind: 'text', value: explanation(cell, translate, language, outcome, say) },
    as_of: cell.as_of === null ? NOT_REPORTED : { kind: 'date', iso: cell.as_of },
    country_key: { kind: 'text', value: cell.country_key },
  };
  return RUN_CELLS_FILE_KEYS.map((key) => cells[key]);
}

/**
 * A row's outcome in words: a size with no forecast by its reason and the window's weeks («Nessuna
 * previsione: nessuna vendita nelle ultime 8 settimane»), every other outcome as {@link outcomeWordsAt}.
 */
export function cellOutcomeWords(code: string, translate: Translate, position: number | null, weeks: number | null): string {
  const reason = noForecastReason(code);
  return reason === null ? outcomeWordsAt(code, translate, position) : noForecastWords({ reason, window_weeks: weeks }, translate);
}

/** The definition the provenance cites: what a row is, and what the file leaves out on purpose. */
const DEFINITION = 'Every store x article x size cell the replenishment run recorded in its stock ledger (GET /inseason/runs/{run_id}/report/replenishment/cells/export, platform ADR 6197), with what became of it: a proposal line written (proposed pairs and the constraint that bound them), no need under one pair (need_below_one_unit: the store already has enough, or its demand is too low, told by the position), already covered by stock, in transit and on order (need_zero_in_transit), a need left uncovered and why (the uncovered reason), stock not reported that day, or not recorded by a run written before the record; and every ranged size the run had no forecast for (platform ADR 6182), in its place among the cells, its outcome code size_without_forecast:<reason> (no_sales_in_window, forecast_absent), with no target or need and the on-hand the run\'s stock batch reported for it on the run\'s stock day. in_transit_and_on_order_units = committed_units - on_hand_units; position_less_target_units = committed_units - target_units; explanation is the outcome in one sentence with the row\'s own figures. Cells the run never reads are not in the file: articles outside the planning scope (e.g. carrier bags), sizes not ranged or not recently active, stores not in the store master. Every measure is nullable and an absence is never a zero.';

/** The citation of the run's parameters: their sentence, in the reader's language. */
const PARAMETERS_MEASURE = 'inseason.runCells.parameters';

// ── the summary sheet, counted from the rows written ─────────────────────────

/** One line of the summary: how many rows, the need they carried and the pairs proposed (`null`: no row carries the figure). */
export interface OutcomeTally {
  readonly code: string;
  readonly cells: number;
  readonly need: number | null;
  readonly proposed: number | null;
  /** The cells whose position (`committed_units`) is above nought: what tells «enough» from «demand too low» (ADR 6204). */
  readonly withPosition: number;
  /** The cells whose need is a pair or more: what makes an outcome «Serve» (ADR 6204). */
  readonly withNeed: number;
}

/** What the file holds, by outcome — counted from exactly the rows the file writes, so the two always agree. */
export interface RunCellsTally {
  /** Every cell the run recorded, and its outcomes in the run's order. */
  readonly recorded: OutcomeTally;
  readonly byOutcome: readonly OutcomeTally[];
  /** The ranged sizes with no forecast among the rows, and their reasons (ADR 6182's codes). */
  readonly unforecast: { readonly total: number; readonly byReason: readonly OutcomeTally[] };
  /** Cells whose stock was reported and is 0 on the shelf — the network-health cockpit's stock-outs. */
  readonly stockOuts: OutcomeTally;
  readonly stockOutsWithLine: OutcomeTally;
  readonly stockOutsWithoutLine: OutcomeTally;
  readonly stockOutsWithoutLineByOutcome: readonly OutcomeTally[];
}

/** The outcomes every summary states, even at 0, in this order; the rest follow in the run's own order, when present. */
const ALWAYS_STATED = [LINE_WRITTEN, NEED_BELOW_ONE_UNIT, NEED_ZERO_IN_TRANSIT, POSITION_NOT_REPORTED] as const;

/** The run's order of the uncovered reasons (`CAUSE_ORDER`), then a run with no record, then codes this build does not know. */
export function outcomeOrder(codes: Iterable<string>): readonly string[] {
  const pipeline = CAUSE_ORDER;
  const rank = (code: string): number => {
    const head = (ALWAYS_STATED as readonly string[]).indexOf(code);
    if (head !== -1) return head;
    const at = pipeline.indexOf(code);
    if (at !== -1) return ALWAYS_STATED.length + at;
    return code === NOT_RECORDED_BY_RUN ? ALWAYS_STATED.length + pipeline.length : ALWAYS_STATED.length + pipeline.length + 1;
  };
  return [...new Set(codes)].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

class Tallies {
  private readonly byCode = new Map<string, { cells: number; need: number | null; proposed: number | null; withPosition: number; withNeed: number }>();

  add(code: string, need: number | null, proposed: number | null, position: number | null = null): void {
    const tally = this.byCode.get(code) ?? { cells: 0, need: null, proposed: null, withPosition: 0, withNeed: 0 };
    tally.cells += 1;
    if (hasPosition(position)) tally.withPosition += 1;
    if (need !== null && need >= 1) tally.withNeed += 1;
    if (need !== null) tally.need = (tally.need ?? 0) + need;
    // A cell with no line proposed nothing: its empty `proposed_units` adds 0 pairs to the sum.
    tally.proposed = (tally.proposed ?? 0) + (proposed ?? 0);
    this.byCode.set(code, tally);
  }

  lines(always: readonly string[] = []): readonly OutcomeTally[] {
    return outcomeOrder([...always, ...this.byCode.keys()]).map((code) => {
      const tally = this.byCode.get(code);
      return {
        code, cells: tally?.cells ?? 0, need: tally?.need ?? (tally === undefined ? 0 : null), proposed: tally?.proposed ?? (tally === undefined ? 0 : null),
        withPosition: tally?.withPosition ?? 0, withNeed: tally?.withNeed ?? 0,
      };
    });
  }

  total(code: string): OutcomeTally {
    let cells = 0;
    let need: number | null = null;
    let proposed: number | null = null;
    let withPosition = 0;
    let withNeed = 0;
    for (const tally of this.byCode.values()) {
      cells += tally.cells;
      withPosition += tally.withPosition;
      withNeed += tally.withNeed;
      if (tally.need !== null) need = (need ?? 0) + tally.need;
      if (tally.proposed !== null) proposed = (proposed ?? 0) + tally.proposed;
    }
    return { code, cells, need: cells === 0 ? 0 : need, proposed: cells === 0 ? 0 : proposed, withPosition, withNeed };
  }
}

/**
 * The file's summary, counted one cell at a time — so a file that is read as it arrives is counted as it
 * arrives, and never held to be counted (`docs/adr/9073`). The same count as {@link tallyRunCells}, which is this over an array.
 */
export class RunCellsTallier {
  private readonly all = new Tallies();
  private readonly outsAll = new Tallies();
  private readonly outsWithLine = new Tallies();
  private readonly outs = new Tallies();
  private readonly unforecast = new Tallies();
  private unforecastTotal = 0;

  add(cell: CellReportRow): void {
    this.all.add(cell.outcome, cell.need_units, cell.proposed_units, cell.committed_units);
    const reason = noForecastReason(cell.outcome);
    if (reason !== null) {
      this.unforecastTotal += 1;
      this.unforecast.add(reason === '' ? SIZE_WITHOUT_FORECAST : reason, null, null);
    }
    // The cockpit's stock-out: the stock was reported and the shelf holds none (DEC-107).
    if (cell.position_state !== 'reported' || cell.on_hand_units !== 0) return;
    this.outsAll.add(cell.outcome, cell.need_units, cell.proposed_units, cell.committed_units);
    (cell.outcome === LINE_WRITTEN ? this.outsWithLine : this.outs).add(cell.outcome, cell.need_units, cell.proposed_units, cell.committed_units);
  }

  /** The tally, with the sizes that had no forecast among the rows beside it. */
  finish(): RunCellsTally {
    return {
      recorded: this.all.total('cells_recorded'),
      byOutcome: this.all.lines(ALWAYS_STATED),
      unforecast: { total: this.unforecastTotal, byReason: this.unforecast.lines() },
      stockOuts: this.outsAll.total('stock_outs'),
      stockOutsWithLine: this.outsWithLine.total('stock_outs_with_line'),
      stockOutsWithoutLine: this.outs.total('stock_outs_without_line'),
      stockOutsWithoutLineByOutcome: this.outs.lines(),
    };
  }
}

/** The file's summary, counted from its rows. Pure. */
export function tallyRunCells(cells: readonly CellReportRow[]): RunCellsTally {
  const tallier = new RunCellsTallier();
  for (const cell of cells) tallier.add(cell);
  return tallier.finish();
}

// ── the two questions (platform ADR 6204, client feedback 2026-10-22) ─────────

/**
 * What became of a cell, as two questions. Does the store need stock? «enough»: stock and arrivals
 * already cover the forecast demand to the next replenishment; «lowDemand»: the store is empty but the
 * forecast does not justify a pair; otherwise it does. If so, could it be sent? «sent»: a proposal line
 * was written; «notSent»: it was not, for the reason the outcome states. Outside both: «notMeasured».
 */
export type Family = 'enough' | 'lowDemand' | 'sent' | 'notSent' | 'notMeasured';

/** The family's code in the CSV (the workbook shows its words). */
export const FAMILY_CODES: Readonly<Record<Family, string>> = {
  enough: 'not_needed_has_enough',
  lowDemand: 'not_needed_low_demand',
  sent: 'needed_sent',
  notSent: 'needed_not_sent',
  notMeasured: 'not_measured',
};

/** A literal table, so every key is a reference the catalogue check sees. */
export const FAMILY_KEYS: Readonly<Record<Family, string>> = {
  enough: 'inseason.runCells.family.enough',
  lowDemand: 'inseason.runCells.family.lowDemand',
  sent: 'inseason.runCells.family.sent',
  notSent: 'inseason.runCells.family.notSent',
  notMeasured: 'inseason.runCells.family.notMeasured',
};

/** The outcomes outside both questions: the position was not measured, not recorded, or the size has no forecast. */
const NOT_MEASURED_OUTCOMES: ReadonlySet<string> = new Set([POSITION_NOT_REPORTED, NOT_RECORDED_BY_RUN, ...Object.keys(NO_FORECAST_KEYS)]);
const notMeasured = (outcome: string): boolean => NOT_MEASURED_OUTCOMES.has(outcome) || noForecastReason(outcome) !== null;

/**
 * The family of an outcome, told by the cell's position where the outcome is `need_below_one_unit`
 * (position above nought: «enough»; otherwise «lowDemand»). Any other outcome is «Serve, non inviata»
 * only when the cell has a need of a pair or more — «Serve» is that invariant, not «every other outcome»:
 * a cell with no need worked out (`forecast_horizon_short`, a refused run) or a need of 0 is «notMeasured»,
 * keeping its own reason words. Pure.
 */
export function familyOf(outcome: string, position: number | null, need: number | null): Family {
  if (outcome === LINE_WRITTEN) return 'sent';
  if (outcome === NEED_ZERO_IN_TRANSIT) return 'enough';
  if (outcome === NEED_BELOW_ONE_UNIT) return hasPosition(position) ? 'enough' : 'lowDemand';
  if (notMeasured(outcome)) return 'notMeasured';
  return need !== null && need >= 1 ? 'notSent' : 'notMeasured';
}

/** One outcome's cells as the funnel takes them: how many, how many have a position, and the pairs. */
export interface FunnelCount {
  readonly code: string;
  readonly cells: number;
  /** The cells whose position is above nought. */
  readonly withPosition: number;
  /** The cells whose need is a pair or more (the platform's `cells_with_need`). */
  readonly withNeed: number;
  readonly need: number | null;
  readonly proposed: number | null;
}

export type FunnelKind = 'total' | 'enough' | 'lowDemand' | 'serve' | 'sent' | 'notSent' | 'notMeasured' | 'notMeasuredCode';

/** A line of the funnel, shared by the screen and the workbook's summary. */
export interface FunnelLine {
  readonly id: string;
  readonly kind: FunnelKind;
  /** The platform code of the line: the outcome for a reason, otherwise the family's. */
  readonly code: string;
  readonly cells: number;
  /** Under «Serve» or under «Non misurata». */
  readonly nested: boolean;
  /** Its share of the total (the «Serve» line) or of «Serve» (the lines under it); `null` elsewhere, and where the base is 0. */
  readonly share: { readonly basis: 'total' | 'serve'; readonly fraction: number } | null;
  /**
   * Pairs requested (the need) and proposed, on the «Serve» lines: «proposta scritta» states both (the proposal can be
   * lower than the need, a line can be capped), a «non inviata» reason its need and 0 proposed, «Serve» the sum of the two
   * kinds of line; `null` elsewhere, and for a need the run did not work out.
   */
  readonly need: number | null;
  readonly proposed: number | null;
}

/**
 * The funnel of a run's cells: the total; the cells that do not need stock (two lines); the ones that
 * do, with their share of the total; of those, the proposals written and each reason one was not sent,
 * with their share of the cells that need stock; and the cells not measured. ONE function over the
 * counts by outcome, which the screen reads from the platform's summary and the file from its own
 * rows, so the two cannot be told differently (UI ADR 9069). Pure.
 */
export function funnelOf(counts: readonly FunnelCount[]): readonly FunnelLine[] {
  let total = 0;
  let enough = 0;
  let lowDemand = 0;
  let notMeasured = 0;
  const sent: FunnelCount = counts.find((count) => count.code === LINE_WRITTEN) ?? { code: LINE_WRITTEN, cells: 0, withPosition: 0, withNeed: 0, need: 0, proposed: 0 };
  const notSent: FunnelCount[] = [];
  const measuredBy: FunnelCount[] = [];
  for (const count of counts) {
    total += count.cells;
    if (count.code === NEED_BELOW_ONE_UNIT) {
      enough += count.withPosition;
      lowDemand += count.cells - count.withPosition;
      continue;
    }
    // An outcome that is neither of the fixed ones is «Serve, non inviata» for the cells that have a need, and «Non misurata» for the rest.
    const family = familyOf(count.code, null, count.withNeed > 0 ? 1 : null);
    if (family === 'enough') enough += count.cells;
    else if (family === 'notMeasured') {
      notMeasured += count.cells;
      // The file states the always-stated outcomes at 0 and the platform omits them: a line of no cells is neither's.
      if (count.cells > 0) measuredBy.push(count);
    } else if (family === 'notSent') {
      notSent.push({ ...count, cells: count.withNeed });
      if (count.cells > count.withNeed) {
        notMeasured += count.cells - count.withNeed;
        measuredBy.push({ ...count, cells: count.cells - count.withNeed, need: null, proposed: null });
      }
    }
  }
  const serve = sent.cells + notSent.reduce((sum, count) => sum + count.cells, 0);
  // «Serve» carries what its lines carry: the need of all of them (not worked out if any line with cells has none) and the pairs proposed on the written ones.
  const serveLines = [sent, ...notSent].filter((count) => count.cells > 0);
  const serveNeed = serveLines.some((count) => count.need === null) ? null : serveLines.reduce((sum, count) => sum + (count.need ?? 0), 0);
  const of = (basis: 'total' | 'serve', cells: number): FunnelLine['share'] => {
    const base = basis === 'total' ? total : serve;
    return base === 0 ? null : { basis, fraction: cells / base };
  };
  const line = (fields: Pick<FunnelLine, 'id' | 'kind' | 'code' | 'cells'> & Partial<FunnelLine>): FunnelLine => ({ nested: false, share: null, need: null, proposed: null, ...fields });
  return [
    line({ id: 'recorded', kind: 'total', code: 'cells_recorded', cells: total }),
    line({ id: 'enough', kind: 'enough', code: FAMILY_CODES.enough, cells: enough }),
    line({ id: 'lowDemand', kind: 'lowDemand', code: FAMILY_CODES.lowDemand, cells: lowDemand }),
    line({ id: 'serve', kind: 'serve', code: 'needed', cells: serve, share: of('total', serve), need: serveNeed, proposed: sent.proposed }),
    line({ id: LINE_WRITTEN, kind: 'sent', code: LINE_WRITTEN, cells: sent.cells, nested: true, share: of('serve', sent.cells), need: sent.need, proposed: sent.proposed }),
    ...outcomeOrder(notSent.map((count) => count.code)).map((code) => {
      const count = notSent.find((entry) => entry.code === code)!;
      // No line was written for a reason: nothing was proposed, whatever the tally says.
      return line({ id: code, kind: 'notSent', code, cells: count.cells, nested: true, share: of('serve', count.cells), need: count.need, proposed: 0 });
    }),
    line({ id: 'notMeasured', kind: 'notMeasured', code: FAMILY_CODES.notMeasured, cells: notMeasured }),
    ...outcomeOrder(measuredBy.map((count) => count.code)).map((code) => line({
      // A reason with cells on both sides keeps one id on each line: the one under «Serve» is the reason's own.
      id: notSent.some((entry) => entry.code === code) ? `notMeasured:${code}` : code, kind: 'notMeasuredCode', code,
      cells: measuredBy.find((entry) => entry.code === code)!.cells, nested: true,
    })),
  ];
}

/** A funnel line in words. `total` is the caller's: the screen and the sheet word the total differently. */
export function funnelWords(entry: FunnelLine, translate: Translate, total: string): string {
  const reason = (): string => outcomeWords(entry.code, (key) => translate(key));
  switch (entry.kind) {
    case 'total': return total;
    case 'enough': return translate(FAMILY_KEYS.enough);
    case 'lowDemand': return translate(FAMILY_KEYS.lowDemand);
    case 'serve': return translate('inseason.runCells.family.serve');
    case 'sent': return translate(FAMILY_KEYS.sent);
    case 'notSent': return translate('inseason.runCells.family.notSentBecause', { reason: reason() });
    case 'notMeasured': return translate(FAMILY_KEYS.notMeasured);
    case 'notMeasuredCode': return reason();
  }
}

/** The funnel's counts from the file's own tally. */
export function funnelCountsOf(byOutcome: readonly OutcomeTally[]): readonly FunnelCount[] {
  return byOutcome.map((row) => ({ code: row.code, cells: row.cells, withPosition: row.withPosition, withNeed: row.withNeed, need: row.need, proposed: row.proposed }));
}

export const RUN_CELLS_SUMMARY_SHEET_KEY = 'inseason.runCells.summary.sheet';

/** The summary sheet's columns: the section, the line in words, its code (in «Tecnico»), then the counts. */
export const RUN_CELLS_SUMMARY_COLUMNS: readonly ExportColumn[] = [
  { key: 'section', headerKey: 'inseason.runCells.summary.column.section', kind: 'text', freeze: true, descriptionKey: 'inseason.columnGuide.runCellsSummary.section' },
  { key: 'line', headerKey: 'inseason.runCells.summary.column.line', kind: 'text', descriptionKey: 'inseason.columnGuide.runCellsSummary.line' },
  { key: 'cells', headerKey: 'inseason.runCells.summary.column.cells', kind: 'integer', descriptionKey: 'inseason.columnGuide.runCellsSummary.cells' },
  { key: 'need_units', headerKey: 'inseason.runCells.summary.column.need', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.runCellsSummary.need' },
  { key: 'proposed_units', headerKey: 'inseason.runCells.summary.column.proposed', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.runCellsSummary.proposed' },
  { key: 'share', headerKey: 'inseason.runCells.summary.column.share', kind: 'percent', descriptionKey: 'inseason.columnGuide.runCellsSummary.share' },
  { key: 'share_of', headerKey: 'inseason.runCells.summary.column.shareOf', kind: 'text', descriptionKey: 'inseason.columnGuide.runCellsSummary.shareOf' },
  { key: 'line_code', headerKey: 'inseason.column.reasonCode', kind: 'text', technical: true, descriptionKey: 'inseason.columnGuide.runCellsSummary.lineCode' },
];

/** The summary as rows of {@link RUN_CELLS_SUMMARY_COLUMNS}. Pure. */
export function runCellsSummaryRows(
  tally: RunCellsTally, translate: Translate, platform: CellsSummaryResponse | null = null,
): readonly (readonly ExportCell[])[] {
  const section = (key: string): ExportCell => ({ kind: 'text', value: translate(key) });
  const line = (sectionKey: string, words: string, row: Pick<OutcomeTally, 'code' | 'cells' | 'need' | 'proposed'>, share: FunnelLine['share'] = null): readonly ExportCell[] => [
    section(sectionKey), { kind: 'text', value: words }, { kind: 'integer', raw: row.cells }, integer(row.need), integer(row.proposed),
    share === null ? NOT_REPORTED : { kind: 'percent', raw: share.fraction },
    share === null ? NOT_REPORTED : { kind: 'text', value: translate(share.basis === 'total' ? 'inseason.runCells.summary.shareOf.total' : 'inseason.runCells.summary.shareOf.serve') },
    { kind: 'text', value: row.code },
  ];
  const words = (code: string): string => outcomeWords(code, (key) => translate(key));
  const CELLS = 'inseason.runCells.summary.section.cells';
  const UNFORECAST = 'inseason.runCells.summary.section.unforecast';
  const OUTS = 'inseason.runCells.summary.section.stockOuts';
  // The funnel opens the sheet, counted from the rows the file writes (the screen's is the platform's, the same function).
  const total = translate('inseason.runCells.summary.line.recorded');
  const rows: (readonly ExportCell[])[] = funnelOf(funnelCountsOf(tally.byOutcome)).map((entry) => line(CELLS, funnelWords(entry, translate, total), entry, entry.share));
  // The rows of sizes with no forecast, by reason: inside the cells above («Non misurata»), and broken down here.
  // A size with no forecast has no need anyone computed (absent), and nothing was proposed for it (0).
  const none = (row: OutcomeTally): OutcomeTally => ({ ...row, need: null, proposed: 0 });
  rows.push(line(UNFORECAST, translate('inseason.runCells.summary.line.unforecast'), none({ code: 'no_forecast', cells: tally.unforecast.total, need: null, proposed: 0, withPosition: 0, withNeed: 0 })));
  for (const row of tally.unforecast.byReason) {
    rows.push(line(UNFORECAST, reasonOnly(row.code, translate), none(row)));
  }
  if (platform !== null) {
    // The platform's stock-outs (ADR 6200): Network health's own, a line OR an inbound transfer covering
    // one — the file knows no transfers, so its own count of «without a line» would read more.
    const count = (code: string, cells: number): OutcomeTally => ({ code, cells, need: null, proposed: null, withPosition: 0, withNeed: 0 });
    rows.push(
      line(OUTS, translate('inseason.runCells.summary.line.stockOuts'), count('stock_outs', platform.stock_outs)),
      line(OUTS, translate('inseason.runCells.summary.line.stockOutsWithLine'), count('stock_outs_with_proposal', platform.stock_outs_with_proposal)),
      line(OUTS, translate('inseason.runCells.summary.line.stockOutsWithTransfer'), count('stock_outs_with_transfer_in', platform.stock_outs_with_transfer_in)),
      line(OUTS, translate('inseason.runCells.summary.line.stockOutsUncovered'), count('stock_outs_uncovered', platform.stock_outs_uncovered)),
      ...outcomeOrder(platform.stock_outs_uncovered_by_outcome.map((row) => row.outcome)).map((code) => line(
        OUTS, words(code), count(code, platform.stock_outs_uncovered_by_outcome.find((row) => row.outcome === code)!.cells),
      )),
    );
    return rows;
  }
  rows.push(
    line(OUTS, translate('inseason.runCells.summary.line.stockOuts'), tally.stockOuts),
    line(OUTS, translate('inseason.runCells.summary.line.stockOutsWithLine'), tally.stockOutsWithLine),
    line(OUTS, translate('inseason.runCells.summary.line.stockOutsWithoutLine'), tally.stockOutsWithoutLine),
    ...tally.stockOutsWithoutLineByOutcome.map((row) => line(OUTS, words(row.code), row)),
  );
  return rows;
}

/** A no-forecast reason in words for the summary: the window is each size's own, so the summary names the reason without it. */
function reasonOnly(code: string, translate: Translate): string {
  return translate(code === 'no_sales_in_window' ? 'inseason.runCells.summary.line.noSales' : code === 'forecast_absent' ? 'inseason.runCells.summary.line.forecastAbsent' : NO_FORECAST_KEY);
}

// ── what the file is read with ───────────────────────────────────────────────

/**
 * What the file's provenance is written from, read once per download: the run's context (in «Tecnico»
 * of the workbook) and its parameters (in the target's meaning). Since the owner's ruling of
 * 2026-10-24 the rows carry no provenance column.
 */
export interface RunCellsProvenance {
  readonly context: ExportContext;
  /** The run's parameters; `null` when its provenance could not be read. */
  readonly parameters: RunCellsParameters | null;
}

/** The provenance a pure caller has when nothing could be read: every field stated absent, never guessed. */
export function unreadRunCellsProvenance(runId: string, generatedAt: string = new Date().toISOString()): RunCellsProvenance {
  return {
    context: { runId, run: null, runAbsence: 'run_directory_unread', platform: null, generatedAt },
    parameters: null,
  };
}

/** What the file reads beside the run's cells, and the language its sentences are written in. */
export interface RunCellsExtras {
  /** The run's cells summary (ADR 6200), for the stock-outs as Network health counts them; `null`/absent: counted from the rows. */
  readonly summary?: CellsSummaryResponse | null;
  readonly language?: string;
}

/**
 * What every row of the run's file is made of — its columns and the words its cells are said in —
 * worked out ONCE per file, then applied a cell at a time: the materialised request (`runCellsRequest`,
 * the workbook) and the streamed one (`runCellsStreamRequest`, the CSVs) write the very same rows.
 */
interface RunCellsShape {
  readonly columns: readonly ExportColumn[];
  readonly cellRow: (cell: CellReportRow) => readonly ExportCell[];
  readonly request: Omit<ExportRequest, 'rows' | 'workbook'>;
}

function runCellsShape(runId: string, translate: Translate, provenance: RunCellsProvenance, extras: RunCellsExtras): RunCellsShape {
  const language = extras.language ?? 'en-GB';
  const sentence = parametersSentence(provenance.parameters, translate, language);
  const columns = runCellsColumns(sentence);
  const cited: CitedMeasure[] = [{ measure: RUN_CELLS_EXPORT_ID, definition: DEFINITION }, { measure: PARAMETERS_MEASURE, definition: sentence }];
  const citations = citeMeasures(cited, provenance.context);
  // A word the catalogue gives without values is asked for once, not once a row: the same words of a column,
  // for 436,950 rows, is 436,950 lookups in i18next (20 µs each) and one answer.
  const remembered = new Map<string, string>();
  const rowTranslate: Translate = (key, values) => {
    if (values !== undefined) return translate(key, values);
    let text = remembered.get(key);
    if (text === undefined) { text = translate(key); remembered.set(key, text); }
    return text;
  };
  // The outcome's words are the same for every cell with its code: worded once per code, not once per row.
  const worded = new Map<string, string>();
  const words = (code: string, position: number | null, weeks: number | null = null): string => {
    // `need_below_one_unit` is worded by the position (above nought or not); a size with no forecast by its window's
    // weeks; the rest by the code alone.
    const at = code === NEED_BELOW_ONE_UNIT && position !== null
      ? `${code}:${hasPosition(position) ? 'position' : 'none'}`
      : noForecastReason(code) === null ? code : `${code}|${weeks ?? ''}`;
    let value = worded.get(at);
    if (value === undefined) {
      value = cellOutcomeWords(code, rowTranslate, position, weeks);
      worded.set(at, value);
    }
    return value;
  };
  const say = sentences(rowTranslate);
  const filters: readonly ProvenanceFilter[] = [{ field: 'run_id', operator: 'equals', value: runId }];
  const base: ExportRequest['provenance'] = {
    filters,
    hierarchyNode: NOT_PUBLISHED,
    timeWindow: NOT_PUBLISHED,
    scenario: NOT_PUBLISHED,
    planVersionId: NOT_PUBLISHED,
    measureDefinitions: citations,
    generatedAt: provenance.context.generatedAt,
    generatedBy: NO_PRINCIPAL,
    platformVersion: buildVersion(),
  };
  return {
    columns,
    cellRow: (cell) => runCellRow(cell, rowTranslate, language, words, say),
    request: {
      id: RUN_CELLS_EXPORT_ID,
      titleKey: RUN_CELLS_TITLE_KEY,
      columns,
      // No `rowCount`, on purpose: the owner ruled ONE file for the whole run (2026-10-20), a run holds
      // hundreds of thousands of cells, and no asynchronous export job exists for it. The rows arrive in
      // one streamed request from the platform, so the browser walks no pages; stating the count would
      // make the export service refuse every run over its synchronous budget.
      // The run, its market and plan version, the platform release — from the same context as the columns.
      provenance: provenanceOfRun(base, provenance.context, citations),
      perimeter: {
        filters,
        // The platform's order: store, article, then size in run order — the sizes with no forecast in their places.
        sort: [],
        visibleColumns: columns.map((column) => column.key),
        drillPath: [],
        densityMode: 'compact',
      },
    },
  };
}

/** The workbook opens on the summary (client request 2026-10-21); the CSV is the rows alone. */
function withWorkbook(shape: RunCellsShape, summary: ReturnType<typeof runCellsSummaryRows>, rows: ExportRequest['rows']): ExportRequest {
  return {
    ...shape.request,
    rows,
    workbook: {
      titleKey: RUN_CELLS_TITLE_KEY,
      sheets: [
        { titleKey: RUN_CELLS_SUMMARY_SHEET_KEY, columns: RUN_CELLS_SUMMARY_COLUMNS, rows: () => summary },
        { titleKey: RUN_CELLS_TITLE_KEY, columns: shape.columns, rows },
      ],
    },
  };
}

/**
 * The run's cells — its ranged sizes with no forecast among them — as the export request of the file's
 * columns, every row held. The workbook opens on «Riepilogo», counted from these same rows, then the
 * rows. For a file of a few rows; the whole run's file is never held (the CSVs stream, {@link runCellsStreamRequest};
 * the workbook reads a kept file, {@link runCellsWorkbookRequest}).
 */
export function runCellsRequest(
  cells: readonly CellReportRow[], runId: string, translate: Translate, provenance: RunCellsProvenance = unreadRunCellsProvenance(runId),
  extras: RunCellsExtras = {},
): ExportRequest {
  const shape = runCellsShape(runId, translate, provenance, extras);
  const rows = cells.map(shape.cellRow);
  return withWorkbook(shape, runCellsSummaryRows(tallyRunCells(cells), translate, extras.summary ?? null), () => rows);
}

/**
 * The run's cells as a file that was read ONCE and is read again whenever the export asks (`docs/adr/9073`): the count and the
 * tally were taken while it arrived, and `cells` reads the kept file from its first row. A run holds hundreds of thousands of
 * cells; the export never holds them.
 */
export interface RunCellsSource {
  /** The cells the file holds. */
  readonly count: number;
  /** The summary's count, taken as the cells arrived. */
  readonly tally: RunCellsTallier;
  /** The cells again, from the first. */
  readonly cells: () => AsyncIterable<CellReportRow>;
  /** What the kept file is, in bytes, and what it costs in memory: for a diagnostic. */
  readonly size: { readonly bytes: number; readonly stored: number };
}

/**
 * The workbook's request, row for row the same file as {@link runCellsRequest} but with the cells read from a kept file
 * ({@link keepCells}) as the workbook asks for them — the writer reads them more than once (it must know the sheet's currencies
 * and which cells carry a reason before it writes the header) — and made into rows one at a time, never held.
 */
export function runCellsWorkbookRequest(
  source: RunCellsSource, runId: string, translate: Translate, provenance: RunCellsProvenance = unreadRunCellsProvenance(runId),
  extras: RunCellsExtras = {},
): ExportRequest {
  const shape = runCellsShape(runId, translate, provenance, extras);
  const rows = (): AsyncIterable<readonly ExportCell[]> => (async function* rowsOfKeptFile() {
    for await (const cell of source.cells()) yield shape.cellRow(cell);
  })();
  return withWorkbook(shape, runCellsSummaryRows(source.tally.finish(), translate, extras.summary ?? null), rows);
}

/**
 * The same file as {@link runCellsRequest}, row for row, for the two CSVs (`docs/adr/9072`): each cell is
 * read from the platform's stream, made a row and handed to the CSV writer, and nothing is kept — the
 * 504,246-row run of 2026-10-23 ran the tab out of memory when its cells, its rows and its text were all
 * held at once. Read ONCE: the rows are the connection. A file that came up short throws at its end
 * (`streamedCells`), so the CSV is never completed and never saved. No workbook: it is held whole.
 */
export function runCellsStreamRequest(
  file: CsvStream, runId: string, translate: Translate, provenance: RunCellsProvenance = unreadRunCellsProvenance(runId),
  extras: RunCellsExtras = {}, onProgress?: CsvProgress,
): ExportRequest {
  const shape = runCellsShape(runId, translate, provenance, extras);
  let read = false;
  return {
    ...shape.request,
    rows: () => {
      if (read) throw new Error('The run’s cells stream once: build the request again to read them again.');
      read = true;
      return (async function* rows() {
        for await (const cell of streamedCells(file, onProgress)) yield shape.cellRow(cell);
      })();
    },
  };
}

/**
 * The most platform cells one workbook holds: Excel's rows on a sheet, less the header row and the totals row — the sizes with no
 * forecast are cells of the platform's file. «Tecnico» has a row for every cell too, and a few more for the file's provenance: a
 * run that fits here but not there is refused by the writer itself (`SheetTooLargeError`), with the same words.
 */
export function runCellsWorkbookRowLimit(): number {
  return EXCEL_MAX_ROWS - 2;
}

/** The run's file, from the platform's stream and the reads beside it — which never stop the download. */
async function openRunCells(runId: string, language: string | undefined): Promise<{
  readonly file: CsvStream; readonly provenance: RunCellsProvenance; readonly extras: RunCellsExtras;
}> {
  const [, file, context, recorded, counted] = await Promise.all([
    // The words of the handover's two causes are the platform's (`./network-health/pull-reasons.ts`): read before any row is written.
    primePullReasonLabels(language),
    callOperationCsvStream(exportCellsReport, { path: { run_id: runId } }, language === undefined ? {} : { language }),
    readExportContext(runId),
    callOperation(runProvenance, { path: { run_id: runId } }).catch(() => null),
    callOperation(cellsSummary, { path: { run_id: runId } }).catch(() => null),
  ]);
  return {
    file,
    provenance: { context, parameters: recorded === null ? null : runCellsParameters(recorded) },
    extras: {
      summary: counted !== null && Array.isArray(counted.stock_outs_uncovered_by_outcome) ? counted : null,
      ...(language === undefined ? {} : { language }),
    },
  };
}

/**
 * The whole run's file for the workbook: ONE request for the platform's CSV, the run's sizes with no forecast (every page), and —
 * beside them — the run's directory row, the platform version and the run's provenance, for the columns that say where the file
 * came from and with which parameters. Those never stop the download: a read that fails is stated absent in `provenance_absent`,
 * and on the summary. The cells are read as they arrive and kept ({@link keepCells}); the workbook is written from the kept file.
 * A run with more cells than a sheet holds is a `RunCellsTooManyRowsError`, said before its rows are read.
 * `onProgress` hears the rows received; `onBuilding` the rows of the kept file the workbook has read since.
 */
export async function runCellsFileRequest(
  runId: string, translate: Translate, language?: string, onProgress?: CsvProgress, onBuilding?: CsvProgress,
): Promise<ExportRequest> {
  const { file, provenance, extras } = await openRunCells(runId, language);
  const source = await keepCells(file, onProgress, onBuilding, runCellsWorkbookRowLimit());
  return runCellsWorkbookRequest(source, runId, translate, provenance, extras);
}

/**
 * The platform's stream, read as it arrives and kept (`docs/adr/9073`). Each cell is read by name, checked, counted and tallied
 * the moment it completes ({@link streamedCells}, the CSVs' own reader); its text is kept compressed ({@link createByteSpool})
 * so the workbook can read the cells again, as often as its passes need, without the minutes of the download and without a
 * 660 MB string. A refusal is the reader's: a column the file lacks, a cell that is not its type (`RunCellsFileError`); a
 * connection cut, or fewer cells than the platform stated, is `ApiIncompleteFileError` (platform ADR 6201) — and then nothing
 * is handed on. `limit`: more cells than this is a {@link RunCellsTooManyRowsError}.
 */
export async function keepCells(file: CsvStream, onProgress?: CsvProgress, onBuilding?: CsvProgress, limit?: number): Promise<RunCellsSource> {
  const spool = createByteSpool();
  const encoder = new TextEncoder();
  const tee: CsvStream = {
    ...file,
    pieces: (async function* kept() {
      for await (const piece of file.pieces) {
        await spool.write(encoder.encode(piece));
        yield piece;
      }
    })(),
  };
  const tally = new RunCellsTallier();
  let count = 0;
  try {
    for await (const cell of streamedCells(tee, onProgress, limit)) {
      tally.add(cell);
      count += 1;
    }
    await spool.close();
  } catch (error) {
    await spool.close().catch(() => undefined);
    throw error;
  }
  const stated = file.statedRows ?? count;
  return {
    count, tally, size: { bytes: spool.bytes, stored: spool.stored },
    cells: () => streamedCells(keptFile(spool, file.status, stated), onBuilding),
  };
}

/** The kept file as a stream again, from its first byte — what {@link streamedCells} reads, checked against the count it held. */
function keptFile(spool: ByteSpool, status: number, count: number): CsvStream {
  const reader = spool.open().getReader();
  return {
    status,
    statedRows: count,
    cancel: async () => { await reader.cancel().catch(() => undefined); },
    pieces: (async function* pieces() {
      const decoder = new TextDecoder();
      for (;;) {
        const next = await reader.read();
        if (next.done) break;
        const piece = decoder.decode(next.value, { stream: true });
        if (piece !== '') yield piece;
      }
      const last = decoder.decode();
      if (last !== '') yield last;
    })(),
  };
}

/**
 * The whole run's file for the two CSVs: the reads of {@link runCellsFileRequest}, the cells streamed
 * into the file as they arrive ({@link runCellsStreamRequest}) — so a run of any size downloads.
 */
export async function runCellsCsvRequest(runId: string, translate: Translate, language?: string, onProgress?: CsvProgress): Promise<ExportRequest> {
  const { file, provenance, extras } = await openRunCells(runId, language);
  return runCellsStreamRequest(file, runId, translate, provenance, extras, onProgress);
}

/**
 * The file's cells, only when they are all there: as many as the platform stated (platform ADR 6201), or an
 * `ApiIncompleteFileError` — a short file is never handed to the export, so it is never saved. No stated count
 * (a platform before ADR 6201): the rows as read, the transport having already refused a cut body.
 */
export function statedCells(file: CsvFile): CellReportRow[] {
  const cells = cellsFromCsv(file.text);
  if (file.statedRows !== null && cells.length !== file.statedRows) {
    throw new ApiIncompleteFileError(file.status, cells.length, file.statedRows);
  }
  return cells;
}

/**
 * The file as an export resolver over the address's run — how the export censuses read it, since no
 * route registers it (the Proposals route registers its table; `../export-resolve-testing.ts`). No
 * run, no read: an empty file that says so in its filter.
 */
export async function runCellsExport(scope?: ExportScope): Promise<ExportRequest> {
  const { runId } = readScope(scope?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  const translate: Translate = (key, values) => i18next.t(key, values ?? {});
  return runId === null ? runCellsRequest([], '', translate) : runCellsFileRequest(runId, translate, i18next.language);
}
